import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FlaskConical, ArrowLeftRight, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { isDemo, startDemoSession, exitDemo } from '@/lib/demo';

const DemoBanner = () => {
  const { t } = useTranslation();
  const { user, hydrateFromToken, logout } = useAuth();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);
  if (!isDemo() || !user) return null;
  const isBuyer = user.role === 'restaurant' || user.role === 'shop';

  const switchRole = async () => {
    setSwitching(true);
    try {
      const data = await startDemoSession(isBuyer ? 'seller' : 'buyer');
      await hydrateFromToken(data.token, data.user);
      navigate('/dashboard');
      toast.success(isBuyer ? t('demo.nowSeller') : t('demo.nowBuyer'));
    } catch {
      toast.error(t('demo.error'));
    } finally {
      setSwitching(false);
    }
  };

  const createAccount = () => {
    sessionStorage.setItem('rs_open_register', '1');
    navigate('/login?register=1', { replace: true });
    exitDemo();
    logout();
  };

  return (
    <div className="bg-amber-400 text-slate-900 border-b border-amber-500" data-testid="demo-banner">
      <div className="max-w-6xl mx-auto px-4 py-2 flex flex-wrap items-center gap-2 sm:gap-3">
        <span className="flex items-center gap-1.5 text-xs sm:text-sm font-semibold">
          <FlaskConical size={16} /> {t('demo.banner')}
        </span>
        <span className="hidden sm:inline text-xs text-amber-900">{t('demo.bannerHint')}</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={switchRole} disabled={switching} data-testid="demo-switch-role" className="h-8 text-xs bg-white/70 border-amber-600 text-slate-900 hover:bg-white">
            {switching ? <Loader2 size={14} className="animate-spin mr-1" /> : <ArrowLeftRight size={14} className="mr-1" />}
            {isBuyer ? t('demo.viewAsSeller') : t('demo.viewAsBuyer')}
          </Button>
          <Button size="sm" onClick={createAccount} data-testid="demo-create-account" className="h-8 text-xs bg-slate-900 hover:bg-slate-800 text-white">
            <UserPlus size={14} className="mr-1" /> {t('demo.createAccount')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoBanner;
